/* eslint-disable import/order */
import '@/@iconify/icons-bundle'
import App from '@/App.vue'
import layoutsPlugin from '@/plugins/layouts'
import vuetify from '@/plugins/vuetify'
import { loadFonts } from '@/plugins/webfontloader'
import router from '@/router'
import * as Sentry from "@sentry/vue";
import i18n from '@/plugins/i18n'
import { createSentryPiniaPlugin } from "@sentry/vue";
import '@core/scss/template/index.scss'
import '@styles/styles.scss'
import 'mobile-drag-drop/default.css'
import { createPinia } from 'pinia'
import { createApp } from 'vue'
import { piniaCapacitorPersist } from 'pinia-plugin-capacitor-persist';
import mitt from 'mitt'
// Importing PWA Elements
import { defineCustomElements } from '@ionic/pwa-elements/loader';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SafeArea } from 'capacitor-plugin-safe-area';
import { sentryBeforeSend } from '@/utils/sentry';
import { reloadOnStaleChunk } from '@/utils/reloadOnStaleChunk';
import { handleUnhandledRejection } from '@/utils/staleChunkRejectionHandler';
import { Capacitor } from '@capacitor/core';
import {useAuthStore} from "@/stores/auth";
import { useUserStore } from '@/stores/user';
import { useConsoleLog } from './stores/consoleLog'
import { initSentryUserTracking } from '@/plugins/sentryUserTracker'
import { scheduleProactiveTokenRefresh } from '@/plugins/authService'
import { createHead } from '@unhead/vue'
import { polyfill } from 'mobile-drag-drop'
import { scrollBehaviourDragImageTranslateOverride } from 'mobile-drag-drop/scroll-behaviour'

polyfill({
  dragImageTranslateOverride: scrollBehaviourDragImageTranslateOverride,
  holdToDrag: 300,
})

loadFonts()

// make sure pinia store is working with capacitor
const pinia = createPinia();
pinia.use(piniaCapacitorPersist);
pinia.use(createSentryPiniaPlugin());

// create the event bus system (docs eventBus is called emitter)
export const emitter = mitt();                   // Initialize mitt

// Create vue app
const app = createApp(App)


// Function to apply safe area insets as CSS variables
const applySafeAreaVariables = async () => {
    console.log(Capacitor.getPlatform())
    if (Capacitor.getPlatform() === 'android' ) {
        try {
            const { insets } = await SafeArea.getSafeAreaInsets();
            document.documentElement.style.setProperty('--safe-area-inset-top', `${insets.top }px`);
            document.documentElement.style.setProperty('--safe-area-inset-right', `${insets.right}px`);
            document.documentElement.style.setProperty('--safe-area-inset-bottom', `${insets.bottom}px`);
            document.documentElement.style.setProperty('--safe-area-inset-left', `${insets.left}px`);

            const { statusBarHeight } = await SafeArea.getStatusBarHeight();
            document.documentElement.style.setProperty('--status-bar-height', `${statusBarHeight}px`);
            console.debug("Android safe area inset is set")

        } catch (e) {
            console.error('Error getting safe area insets for android:', e);
            // Fallback if plugin errors
            document.documentElement.style.setProperty('--safe-area-inset-top', '0px');
            document.documentElement.style.setProperty('--safe-area-inset-right', '0px');
            document.documentElement.style.setProperty('--safe-area-inset-bottom', '0px');
            document.documentElement.style.setProperty('--safe-area-inset-left', '0px');
            document.documentElement.style.setProperty('--status-bar-height', '0px');
        }

        await StatusBar.setOverlaysWebView({ overlay: false });
        await StatusBar.setStyle({ style: Style.Default });
        await StatusBar.setBackgroundColor({ color: '#F7F7F9' });
    }
    else if (Capacitor.getPlatform() === 'ios' ) {
        try {
            const { insets } = await SafeArea.getSafeAreaInsets();
            document.documentElement.style.setProperty('--safe-area-inset-top', `${insets.top}px`);
            document.documentElement.style.setProperty('--safe-area-inset-right', `${insets.right}px`);
            document.documentElement.style.setProperty('--safe-area-inset-bottom', `${insets.bottom}px`);
            document.documentElement.style.setProperty('--safe-area-inset-left', `${insets.left}px`);

            const { statusBarHeight } = await SafeArea.getStatusBarHeight();
            document.documentElement.style.setProperty('--status-bar-height', `${statusBarHeight}px`);
            console.debug("IOS safe area inset is set")

        } catch (e) {
            console.error('Error getting safe area insets:', e);
            // Fallback if plugin errors
            document.documentElement.style.setProperty('--safe-area-inset-top', '0px');
            document.documentElement.style.setProperty('--safe-area-inset-right', '0px');
            document.documentElement.style.setProperty('--safe-area-inset-bottom', '0px');
            document.documentElement.style.setProperty('--safe-area-inset-left', '0px');
            document.documentElement.style.setProperty('--status-bar-height', '0px');
        }
    }
    else
    {
        // Fallback for web environment (browser)
        document.documentElement.style.setProperty('--safe-area-inset-top', 'env(safe-area-inset-top, 0px)');
        document.documentElement.style.setProperty('--safe-area-inset-right', 'env(safe-area-inset-right, 0px)');
        document.documentElement.style.setProperty('--safe-area-inset-bottom', 'env(safe-area-inset-bottom, 0px)');
        document.documentElement.style.setProperty('--safe-area-inset-left', 'env(safe-area-inset-left, 0px)');
        document.documentElement.style.setProperty('--status-bar-height', '0px');
        console.debug("web safe area inset is set")
    }
};

applySafeAreaVariables().then(
    () => {
        if (Capacitor.getPlatform() === 'android' || Capacitor.getPlatform() === 'ios') {
            SafeArea.addListener('safeAreaChanged', (data) => {
                const { insets } = data;
                document.documentElement.style.setProperty('--safe-area-inset-top', `${insets.top}px`);
                document.documentElement.style.setProperty('--safe-area-inset-right', `${insets.right}px`);
                document.documentElement.style.setProperty('--safe-area-inset-bottom', `${insets.bottom}px`);
                document.documentElement.style.setProperty('--safe-area-inset-left', `${insets.left}px`);
            });
        }
    }
);
const head = createHead();
// Use plugins
app.use(vuetify)
app.use(pinia)
app.use(router)
app.use(layoutsPlugin)
app.use(i18n)
app.use(head)
app.provide('emitter', emitter);          // ✅ Provide as `emitter`
app.config.globalProperties.emitter = emitter
// We make the emitter available on window object
// (optional but can be handy, if you have js files 'outside' of vue scope that needs that emitter)
window.emitter = emitter;

const consoleLogInfo = useConsoleLog()
// sentry error tracking initialization in production only
if(import.meta.env.PROD && (Capacitor.getPlatform() === 'web' || consoleLogInfo.getIsSentryEnabled)) {
    Sentry.init({
        app,
        dsn: "https://7b62a63354fcc096fadd57ccd3547680@o4508154345422848.ingest.de.sentry.io/4508154416070736",
        integrations: [
            Sentry.browserTracingIntegration({router})
        ],
        environment: import.meta.env.VITE_APP_ENVIRONMENT || import.meta.env.MODE,
        release: import.meta.env.VITE_APP_VERSION,
        beforeSend: sentryBeforeSend,
    });
    console.debug("Sentry is initialized in production mode")
}

// Initialise Sentry user tracking if already authenticated.
// renewData() is intentionally NOT called here — the router guard handles it
// after verifying / refreshing the token, preventing a double-refresh race
// condition where main.ts API calls and the router guard both attempt to
// refresh an expired token simultaneously with the same refresh token.
const authStore = useAuthStore();
if(authStore.getIsLoggedInState) {
    initSentryUserTracking();
    scheduleProactiveTokenRefresh(Number(authStore.tokenExp));
}

// Recover from stale Vite chunks after a new deploy (DEVAMS-1844).
// When the browser has a cached tab from before a deploy, old chunk URLs
// (content-hashed) no longer exist → "Failed to fetch dynamically imported module".
// All three handlers (vite:preloadError, unhandledrejection, router.onError) call the
// same reloadOnStaleChunk() guard so only the first one fires — prevents competing
// navigations that raced each other in the original fix (DEVAMS-1867).
// Guarded to web only — Capacitor mobile loads a local bundle and is unaffected.
if (Capacitor.getPlatform() === 'web') {
    // vite:preloadError covers <link rel="modulepreload"> and CSS preload failures.
    window.addEventListener('vite:preloadError', () => {
        reloadOnStaleChunk()
    })
    // unhandledrejection covers dynamic imports inside components that are NOT
    // caught by vite:preloadError or router.onError (e.g. a component on the
    // current page that lazily imports a now-stale chunk).
    window.addEventListener('unhandledrejection', (event) => {
        handleUnhandledRejection(event, reloadOnStaleChunk)
    })
}

// Mount vue app
router.isReady().then(() => {
    app.mount('#app')
})

// PWA Elements
defineCustomElements(window);

export default app;
